import * as HtmlToImage from "html-to-image";

type ExportOptions = Parameters<typeof HtmlToImage.toPng>[1];

/**
 * PNG export of a live DOM subtree.
 *
 * html-to-image clones the subtree into an SVG `foreignObject` and loads that
 * SVG through an `<img>`. The image is its own document: it cannot reach the
 * page's fonts, so every `@font-face` it needs has to travel inside it. The
 * clone also carries each element's measured `width` and `height` as inline
 * pixel values, so a font the SVG cannot load is substituted and the
 * substituted text reflows inside a box sized for Inter — overflowing its
 * background, or breaking mid-word.
 *
 * The embedded CSS carries the Inter woff2 files base64-encoded, so it is
 * fetched and encoded once per session rather than once per export. Every
 * export subtree inherits the same font stack, so whichever one is exported
 * first yields the faces the rest need.
 */
export async function exportNodeToPng(node: HTMLElement, options?: ExportOptions): Promise<string> {
  return await HtmlToImage.toPng(node, {cacheBust: true, ...options, fontEmbedCSS: await embeddedFontCSS(node)});
}

let fontEmbedCSS: Promise<string> | undefined;

function embeddedFontCSS(node: HTMLElement): Promise<string> {
  // A failure is not cached, so retrying an export refetches rather than
  // replaying the rejection for the rest of the session.
  fontEmbedCSS ??= HtmlToImage.getFontEmbedCSS(node).catch((error: unknown) => {
    fontEmbedCSS = undefined;
    throw error;
  });
  return fontEmbedCSS;
}
